import { render } from 'preact'
import App from './app.jsx'
import { AppStage } from './components/AppStage.jsx'
import LanguageRoot from './localization/LanguageRoot.jsx'
import './index.css'
import './print.css'
import { applyThemePreference, getStoredThemePreference } from './utils/themePreference'
import { applyBrightness, onGraphicsChange } from './utils/game/graphicsSettings'
import { hydratePrefs } from './utils/persistentPrefs'
import { hydrateCustomMusic } from './utils/arena/customMusicManager'
import { hydrateAmbience } from './utils/arena/ambienceManager'
import { installClipboardFallback } from './utils/clipboardFallback'
import { installSmoothCaret } from './utils/smoothCaret'
import { pruneLocalStorage } from './utils/localStorageQuota'
import { installOverlayScrollbars } from './utils/overlayScrollbars'
import { isWeb, isPhoneLayout, isUnsupportedScreen } from './utils/platform'
import UnsupportedScreen from './components/UnsupportedScreen.jsx'
import { armInstallPrompt } from './utils/installPrompt'
import { hapticTick } from './utils/haptics'

// Phone-mode CSS hooks (tap-highlight, overscroll, user-select rules in
// index.css key off this class) must be on <html> before first paint.
if (isPhoneLayout()) {
  document.documentElement.classList.add('is-phone')
  // Every tappable in the app answers the press itself with a haptic
  // tick. One capture-phase delegate instead of wiring each button;
  // pointerdown so the tick lands at press time, like a native control.
  document.addEventListener('pointerdown', (e) => {
    if (e.target?.closest?.('button, [role="button"]')) hapticTick()
  }, { capture: true, passive: true })
}

// Chrome fires beforeinstallprompt early; the listener must exist before
// the event or the install button can never appear (see installPrompt.js).
if (isWeb()) armInstallPrompt()

applyThemePreference(getStoredThemePreference())

// Native scrollbars are hidden app-wide (index.css); this floats an
// overlay thumb above whatever element is scrolling instead.
installOverlayScrollbars()

// Disable native browser right-click context menu.
// Elements with a custom context menu handler (data-custom-context or canvas)
// still work because their handlers call stopPropagation before this fires.
document.addEventListener('contextmenu', (e) => {
  // Allow right-click on text inputs for native copy/paste
  const tag = e.target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
  e.preventDefault();
});

// The zoom/gesture/Escape suppression below is right for the sealed
// desktop shell but user-hostile in a real browser tab (it would block
// legitimate page zoom and pinch), so it's desktop-only.
if (!isWeb()) {
  // Prevent Escape from exiting fullscreen (browser default).
  // Capture phase fires before the browser's built-in fullscreen exit handler.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // The app's own Escape handler in app.jsx will still fire
      // because it listens on the same phase and is registered later.
    }
  }, true);

  // Disable browser-level zoom: trackpad pinch, Ctrl/Cmd + wheel, and the
  // Ctrl/Cmd + (+/-/0) keyboard shortcuts. Valkenhall is a desktop game, not
  // a web page. The medieval theme has its own viewport scale system, and a
  // stray pinch-to-zoom would warp the entire UI out of alignment.
  // Chromium delivers trackpad pinch as a `wheel` event with `ctrlKey: true`,
  // so a single wheel listener catches both the gesture and the modifier+wheel.
  window.addEventListener('wheel', (e) => {
    if (e.ctrlKey) e.preventDefault();
  }, { passive: false, capture: true });

  window.addEventListener('keydown', (e) => {
    if (!(e.metaKey || e.ctrlKey)) return;
    // `=` covers Cmd+= which most keyboards send for "zoom in" (no Shift required).
    if (e.key === '+' || e.key === '-' || e.key === '_' || e.key === '=' || e.key === '0') {
      e.preventDefault();
    }
  }, true);

  // Safari-style gesture events. Chromium normally suppresses these, but on
  // some macOS builds it forwards them. Defensive coverage costs nothing.
  ['gesturestart', 'gesturechange', 'gestureend'].forEach((evt) => {
    document.addEventListener(evt, (e) => e.preventDefault());
  });

  // CEF on Windows doesn't reliably forward Ctrl+C/V/X/A to the renderer
  // inside editable fields, so we bind those shortcuts ourselves through
  // the Clipboard API. Scoped to INPUT/TEXTAREA/contenteditable only.
  installClipboardFallback();
}

// Monaco-style smooth sliding caret across every input in the app.
installSmoothCaret();

// Disable native browser tooltips globally.
// The browser renders a tooltip for any element with a `title` attribute, which
// looks out of place in a game. Strip `title` on insertion and on mutation so
// Preact renders, third-party components (sonner, framer-motion, shadcn), and
// runtime-added titles are all covered without hardcoding per element.
;(() => {
  const stripTitlesIn = (node) => {
    if (node.nodeType !== 1) return;
    if (node.hasAttribute('title')) node.removeAttribute('title');
    const descendants = node.querySelectorAll('[title]');
    for (let i = 0; i < descendants.length; i++) descendants[i].removeAttribute('title');
  };

  stripTitlesIn(document.documentElement);

  new MutationObserver((mutations) => {
    for (let i = 0; i < mutations.length; i++) {
      const m = mutations[i];
      if (m.type === 'attributes') {
        m.target.removeAttribute('title');
      } else {
        m.addedNodes.forEach(stripTitlesIn);
      }
    }
  }).observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['title'],
  });
})();

// Hydrate preferences from the embedded Bun main process's file-backed
// store BEFORE first render so settings (sound, graphics, controls,
// discord, ambience, playmat) always show their saved values on the
// very first frame. This is what makes settings actually persist.
// localStorage alone is partitioned per-origin (host+port), which
// desktop app restarts can invalidate; the file in OS app-data dir is
// permanent.
// Bound redundant localStorage caches on launch so a full quota can't silently
// break the app's own saves. Authoritative copies live server-side (chat) and
// on disk (sessions), so this loses no data.
pruneLocalStorage();

await hydratePrefs().catch(() => {});

// Custom music must hydrate (and auto-resume if it was playing) BEFORE
// the first playMusic() call in app.jsx fires, so the built-in
// arena-hub track never briefly leaks in over the player's chosen
// custom track.
await hydrateCustomMusic().catch(() => {});

// Ambience auto-resumes the same way custom music does. If the user
// had it playing at last shutdown, start it again before first render.
hydrateAmbience();

// Apply the saved brightness on startup, and re-apply whenever the user
// adjusts the slider in Settings. The CSS filter on <html> covers the
// entire viewport. 3D canvas and 2D UI alike. Runs after hydrate so
// the initial read sees the real saved value.
applyBrightness();
onGraphicsChange(() => applyBrightness());

// Touch devices below the 13-inch iPad Pro class get a static gate
// instead of the app: the arena's texture/audio footprint is what gets
// small-device tabs jetsam-killed, so don't even start loading it.
render(
  isUnsupportedScreen() ? (
    <UnsupportedScreen />
  ) : (
    <LanguageRoot render={(languageVersion) => (
      <AppStage languageVersion={languageVersion}>
        <App languageVersion={languageVersion} />
      </AppStage>
    )} />
  ),
  document.getElementById('app'),
)
